// src/data/demoPitchData.ts

export const demoPitchData = {
  // Core information
  companyName: "EcoTrack Solutions",
  tagline: "AI-powered carbon footprint tracking for conscious consumers",
  
  // Problem
  problem: `Climate change is the defining crisis of our generation, yet individuals feel powerless to make a difference. Current carbon tracking apps are either too complex (requiring manual data entry) or too vague (providing generic estimates).

Key pain points:
- 78% of consumers want to reduce their carbon footprint but don't know where to start
- Existing apps require 15+ minutes of daily manual data entry
- No real-time feedback on purchasing decisions
- Lack of personalized, actionable recommendations

Customer interviews (47 conducted) revealed:
- "I want to know which product is better when I'm standing in the aisle, not after I get home"
- "The apps make me feel guilty without giving me solutions"
- "I'd pay for something that actually helps me make better choices"`,
  
  // Solution
  solution: `EcoTrack is a mobile app that uses AI and computer vision to automatically track carbon footprint:

Key Features:
1. **Smart Receipt Scanning**: Take a photo of any receipt - our AI extracts items and calculates carbon impact in seconds
2. **Product Barcode Scanner**: Scan products in-store for instant carbon comparison
3. **Automated Tracking**: Connects to bank accounts to categorize spending by carbon impact
4. **Personalized Recommendations**: AI suggests lower-carbon alternatives based on your shopping patterns
5. **Community Challenges**: Gamified carbon reduction goals with friends

Technical Implementation:
- Computer vision model trained on 100k+ receipts (92% accuracy)
- Carbon database with 50k+ products
- Bank-level security for financial data connection
- Machine learning for personalized recommendations`,
  
  // Market Size
  marketSize: `**Total Addressable Market (TAM): $12.8B**
- Global carbon tracking apps market growing at 23% CAGR
- 320M potential users in US/EU alone

**Serviceable Available Market (SAM): $3.4B**
- Consumers actively seeking carbon reduction tools
- Premium app users willing to pay $5-10/month

**Serviceable Obtainable Market (SOM): $180M**
- Initial focus on eco-conscious millennials (ages 25-40)
- 15M potential users in our target demographic
- 5% market penetration target in Year 3

Market Trends:
- 73% of millennials willing to pay more for sustainable products
- EU's "Right to Repair" legislation driving environmental awareness
- Corporate ESG reporting requirements creating B2B opportunities`,
  
  // Business Model
  businessModel: `**Revenue Streams:**

1. **Freemium App** (Projected 60% of revenue)
   - Free: Basic tracking + monthly footprint report
   - Premium: $5.99/month or $59.99/year
     * Real-time purchase comparisons
     * Personalized recommendations
     * Community challenges
     * Tax receipt organization

2. **B2B Partnerships** (Projected 30% of revenue)
   - White-label solution for banks ($50k/year per partner)
   - Corporate wellness programs ($10/user/year)
   - ESG reporting tools for enterprises

3. **Affiliate Revenue** (Projected 10% of revenue)
   - Commission on sustainable product recommendations
   - Partner with eco-friendly brands

**Unit Economics:**
- Customer Acquisition Cost (CAC): $15
- Lifetime Value (LTV): $180 (24 months avg retention)
- LTV/CAC Ratio: 12x (excellent)
- Gross Margin: 82%`,
  
  // Team
  team: `**Founding Team:**

**Sarah Chen - CEO & Co-founder**
- Former Product Lead at CarbonCalc (acquired by Salesforce)
- 8 years experience in sustainability tech
- MBA from Stanford GSB
- Passionate about climate tech since college

**Michael Rodriguez - CTO & Co-founder**
- Lead Engineer at Stripe (payments infrastructure)
- 10 years full-stack development experience
- Built scalable systems handling $1B+ transactions
- MS Computer Science from MIT

**Dr. James Wilson - Head of Climate Science**
- PhD in Environmental Science from UC Berkeley
- Former researcher at IPCC
- Published 15+ papers on carbon measurement
- Ensures scientific accuracy of our algorithms

**Advisory Board:**
- Former CEO of Whole Foods (Strategic Advisor)
- Partner at leading climate VC firm
- Chief Sustainability Officer from major retailer`,
  
  // Traction
  traction: {
    customerInterviews: 47,
    waitlistSignups: 1250,
    lettersOfIntent: 8,
    pilotCustomers: 3,
    mvpStatus: 'beta',
    mvpUrl: 'https://testflight.apple.com/join/ecotrack',
    monthlyActiveUsers: 450,
    revenueRunRate: 2400,
  },
  
  // Competition
  competitive: {
    directCompetitors: [
      'Joro App',
      'Sustainably',
      'Oroeco',
      'Capture',
      'Earth Hero'
    ],
    indirectCompetitors: [
      'Mint (personal finance)',
      'MyFitnessPal',
      'Good On You (fashion)'
    ],
    competitiveAdvantage: `Our key advantages:
1. **Automated tracking** - No manual entry required
2. **Real-time comparisons** - Make decisions at point of purchase
3. **Scientific accuracy** - PhD-led methodology
4. **Gamification** - Social features drive engagement
5. **B2B partnerships** - Multiple revenue streams`,
    barriersToEntry: [
      'Patent-pending receipt scanning algorithm',
      'Exclusive partnerships with 3 major banks',
      'Carbon database with 50k+ verified products',
      'Scientific advisory board credibility'
    ],
    substituteProducts: [
      'Manual spreadsheets',
      'General guilt',
      'Doing nothing'
    ],
  },
  
  // Financial
  financial: {
    fundingAsk: 750000,
    useOfFunds: {
      product: 35,
      marketing: 30,
      team: 20,
      operations: 10,
      legal: 3,
      other: 2,
    },
    runwayMonths: 18,
    burnRate: 41667,
    revenueProjection: {
      month1: 5000,
      month3: 15000,
      month6: 45000,
      month12: 120000,
    },
    breakevenMonth: 16,
  },
  
  // GTM
  gtm: {
    customerChannels: [
      'Content Marketing (blog, newsletter)',
      'Social Media (Instagram, TikTok)',
      'Influencer Partnerships',
      'App Store Optimization',
      'PR Campaigns',
      'University Partnerships'
    ],
    salesStrategy: `Freemium app with viral loops:
- Share your footprint on social media
- Challenge friends to reduce together
- Team challenges at companies
- Referral program (1 month free per referral)`,
    marketingPlan: `**Phase 1 (Months 1-3):** Influencer partnerships with eco-conscious creators (200k+ combined followers)
**Phase 2 (Months 4-6):** PR campaign around Earth Day, app store features
**Phase 3 (Months 7-12):** Paid acquisition (Instagram/Facebook ads), university partnerships`,
    partnerships: [
      'Major US Bank (term sheet signed)',
      'Sustainable fashion brand',
      'Zero-waste grocery chain'
    ],
    targetEarlyAdopters: `Eco-conscious millennials in urban areas:
- Ages 25-40
- College educated
- Active on social media
- Already buy sustainable products
- Use fitness/wellness apps`,
  },
  
  // IP
  ip: {
    patentStatus: 'provisional',
    patentNumbers: ['63/123,456', '63/123,457'],
    proprietaryTechnology: `Our core technology includes:
1. **ReceiptScan AI** - Computer vision model trained on 100k+ receipts
2. **CarbonDB** - Proprietary database of 50k+ product carbon footprints
3. **EcoScore Algorithm** - Machine learning model for personalized recommendations`,
    technicalArchitecture: `**Frontend:** React Native (iOS/Android)
**Backend:** Node.js, PostgreSQL, Redis
**AI/ML:** Python, TensorFlow, AWS SageMaker
**Infrastructure:** AWS (EC2, S3, RDS)
**Security:** Bank-level encryption, SOC2 compliance`,
    developmentStage: 'beta',
    techStack: [
      'React Native',
      'Node.js',
      'PostgreSQL',
      'TensorFlow',
      'AWS',
      'Python'
    ],
  },
  
  // Risks
  risks: {
    regulatoryHurdles: [
      'Data privacy regulations (GDPR/CCPA)',
      'Financial data connection compliance',
      'Carbon credit verification standards'
    ],
    complianceRequirements: [
      'SOC2 Type II certification',
      'GDPR compliance',
      'Bank-level security audits'
    ],
    keyRisks: [
      {
        risk: 'Competitors copy our features',
        impact: 'medium',
        mitigation: 'Continuous innovation, patent protection, exclusive partnerships'
      },
      {
        risk: 'User adoption slower than expected',
        impact: 'high',
        mitigation: 'Diversified acquisition channels, B2B revenue stream as backup'
      },
      {
        risk: 'Scientific methodology challenged',
        impact: 'medium',
        mitigation: 'PhD-led team, peer-reviewed research, advisory board credibility'
      },
      {
        risk: 'Data security breach',
        impact: 'high',
        mitigation: 'Bank-level security, regular audits, bug bounty program'
      }
    ],
    marketRisks: [
      'Economic downturn reduces willingness to pay',
      'New regulations change carbon calculation standards',
      'Big tech enters the space'
    ],
    executionRisks: [
      'Hiring challenges for specialized AI talent',
      'Integration delays with bank partners',
      'Mobile app store approval delays'
    ],
  },
};